import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLongPress } from "@/hooks/useLongPress";
import { apiClient } from "@/lib/api/client";
import { API_ENDPOINTS } from "@/constants/api";
import { useAuthStore } from "@/store/authStore";

interface ExitKioskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;
const LONG_PRESS_MS = 5_000;

export const ExitKioskDialog = ({ open, onOpenChange }: ExitKioskDialogProps) => {
  const exitKioskMode = useAuthStore((state) => state.exitKioskMode);
  const [password, setPassword] = useState("");
  const [emergencyCode, setEmergencyCode] = useState("");
  const [showEmergencyField, setShowEmergencyField] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if(!open){
      setPassword("");
      setEmergencyCode("");
      setShowEmergencyField(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if(!lockedUntil){
      setRemainingSeconds(0);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setRemainingSeconds(remaining);

      if(remaining === 0){
        setLockedUntil(null);
        setFailedAttempts(0);
      }
    };

    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const isLocked = lockedUntil !== null && remainingSeconds > 0;

  const handleSuccess = () => {
    exitKioskMode();
    setPassword("");
    setEmergencyCode("");
    setFailedAttempts(0);
    setLockedUntil(null);
    onOpenChange(false);

    if(typeof document !== "undefined" && document.fullscreenElement && document.exitFullscreen){
      document.exitFullscreen().catch(() => {
        /* ignore — nothing we can do if the browser refuses */
      });
    }
  };

  const handleFailure = (message: string) => {
    const nextAttempts = failedAttempts + 1;
    setFailedAttempts(nextAttempts);
    setError(message);
    setPassword("");
    setEmergencyCode("");

    if(nextAttempts >= MAX_ATTEMPTS){
      setLockedUntil(Date.now() + LOCKOUT_MS);
    }
  };

  const handleSubmitPassword = async () => {
    if(isLocked || isSubmitting || !password){
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post(API_ENDPOINTS.VERIFY_PASSWORD, { password });
      handleSuccess();
    }
    catch(err){
      const status = (err as AxiosError).response?.status;
      const message = status === 401 ? "Incorrect password" : "Unable to verify. Please try again.";
      handleFailure(message);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEmergencyCode = async () => {
    if(isLocked || isSubmitting || !emergencyCode){
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post(API_ENDPOINTS.KIOSK_BYPASS, { code: emergencyCode });
      handleSuccess();
    }
    catch(err){
      const status = (err as AxiosError).response?.status;
      const message = status === 401 ? "Incorrect code" : "Unable to verify. Please try again.";
      handleFailure(message);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const longPressHandlers = useLongPress({
    ms: LONG_PRESS_MS,
    onLongPress: () => setShowEmergencyField(true),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              {...longPressHandlers}
              className="inline-flex items-center gap-2 select-none cursor-default"
              aria-label="Attendance Mode"
            >
              <Lock className="h-5 w-5 text-brand" />
              Exit Attendance Mode
            </span>
          </DialogTitle>
          <DialogDescription>
            Enter the supervisor password to unlock the device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="kiosk-exit-password">
              Password
            </label>
            <Input
              id="kiosk-exit-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if(event.key === "Enter"){
                  event.preventDefault();
                  handleSubmitPassword();
                }
              }}
              disabled={isLocked || isSubmitting}
              placeholder="Supervisor password"
            />
          </div>

          {showEmergencyField && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="kiosk-exit-code">
                Emergency Code
              </label>
              <Input
                id="kiosk-exit-code"
                type="password"
                autoComplete="off"
                value={emergencyCode}
                onChange={(event) => setEmergencyCode(event.target.value)}
                onKeyDown={(event) => {
                  if(event.key === "Enter"){
                    event.preventDefault();
                    handleSubmitEmergencyCode();
                  }
                }}
                disabled={isLocked || isSubmitting}
                placeholder="Enter emergency code"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSubmitEmergencyCode}
                disabled={isLocked || isSubmitting || !emergencyCode}
              >
                Use Emergency Code
              </Button>
            </div>
          )}

          {error && !isLocked && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          {isLocked && (
            <p className="text-sm text-destructive" role="alert">
              Too many attempts. Try again in {remainingSeconds}s.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmitPassword} disabled={isLocked || isSubmitting || !password}>
            {isSubmitting ? "Verifying..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
